import React, { createContext, useContext, useEffect, useState } from 'react'
import { simpleNotificationManager } from '../../../packages/@2cmstudio/notifications/SimpleNotificationManager'

interface SimpleNotificationContextType {
  isSupported: boolean
  permission: NotificationPermission
  requestPermission: () => Promise<NotificationPermission>
  sendNotification: (data: any, delay?: number) => Promise<void>
  clearAllNotifications: () => Promise<void>
  clearNotificationsForChallenge: (challengeId: string) => Promise<void>
  getStatus: () => any
}

const SimpleNotificationContext = createContext<SimpleNotificationContextType | null>(null)

export function useSimpleNotifications() {
  const context = useContext(SimpleNotificationContext)
  if (!context) {
    throw new Error('useSimpleNotifications must be used within a SimpleNotificationProvider')
  }
  return context
}

interface SimpleNotificationProviderProps {
  children: React.ReactNode
}

export function SimpleNotificationProvider({ children }: SimpleNotificationProviderProps) {
  const [isSupported, setIsSupported] = useState(false)
  const [permission, setPermission] = useState<NotificationPermission>('default')

  useEffect(() => {
    // Initialize notification manager
    const status = simpleNotificationManager.getStatus()
    setIsSupported(status.supported)
    setPermission(status.permission)

    // Set up periodic permission checks
    const checkPermission = () => {
      const currentPermission = simpleNotificationManager.getPermissionStatus()
      setPermission(currentPermission)
    }

    // Check permission every 5 seconds
    const interval = setInterval(checkPermission, 5000)

    // Listen for storage changes (in case permission changes in another tab)
    const handleStorageChange = () => {
      checkPermission()
    }

    window.addEventListener('storage', handleStorageChange)

    return () => {
      clearInterval(interval)
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [])

  const requestPermission = async (): Promise<NotificationPermission> => {
    const newPermission = await simpleNotificationManager.requestPermission()
    setPermission(newPermission)
    
    // Store permission status
    localStorage.setItem('notification-permission-asked', 'true')
    if (newPermission === 'granted') {
      localStorage.setItem('notifications-enabled', 'true')
    }
    
    return newPermission
  }

  const sendNotification = async (data: any, delay: number = 0): Promise<void> => {
    await simpleNotificationManager.sendNotification(data, delay)
  }

  const clearAllNotifications = async (): Promise<void> => {
    await simpleNotificationManager.clearAllScheduledNotifications()
  }

  const clearNotificationsForChallenge = async (challengeId: string): Promise<void> => {
    await simpleNotificationManager.clearNotificationsForChallenge(challengeId)
  }

  const getStatus = () => {
    return simpleNotificationManager.getStatus()
  }

  const value: SimpleNotificationContextType = {
    isSupported,
    permission,
    requestPermission,
    sendNotification,
    clearAllNotifications,
    clearNotificationsForChallenge,
    getStatus
  }

  return (
    <SimpleNotificationContext.Provider value={value}>
      {children}
    </SimpleNotificationContext.Provider>
  )
} 